"use client";

import { DEAL_CONSTRAINTS } from "../../../lib/dealConstraints";
import { PROPERTY_TYPE_OPTIONS } from "@/lib/listing-form";
import type { Currency } from "@/lib/currency";
import type { DealConstraintId } from "../../../lib/dealConstraints";
import type { DealType } from "@/lib/dealTypes";
import type { FinancingFormFields } from "@/lib/financingTerms";
import type {
  ListingFormFieldError,
  ListingFormValues,
} from "@/lib/listing-form";
import {
  FieldError,
  FieldGrid,
  FieldLabel,
  TextInput,
  TextSelect,
  TextTextarea,
} from "./ListingFormFields";
import { listingFormStyles as s } from "./listingFormStyles";
import { theme } from "@/lib/uiTheme";
import DealTypeSelector from "./DealTypeSelector";
import FinancingFields from "./FinancingFields";

type Props = {
  values: ListingFormValues;
  errors: ListingFormFieldError[];
  disabled?: boolean;
  onFieldChange: <K extends keyof ListingFormValues>(
    field: K,
    value: ListingFormValues[K],
  ) => void;
  onDealTypeChange: (dealType: DealType) => void;
  onFinancingChange: <K extends keyof FinancingFormFields>(
    field: K,
    value: FinancingFormFields[K],
  ) => void;
};

const CURRENCIES: Currency[] = ["USD", "CAD", "GBP", "AUD"];

function errFor(
  errors: ListingFormFieldError[],
  field: string,
): string | undefined {
  return errors.find((e) => e.field === field)?.message;
}

export default function StepDetails({
  values,
  errors,
  disabled,
  onFieldChange,
  onDealTypeChange,
  onFinancingChange,
}: Props) {
  function toggleConstraint(id: DealConstraintId) {
    const has = values.deal_constraints.includes(id);
    const next = has
      ? values.deal_constraints.filter((c) => c !== id)
      : [...values.deal_constraints, id];
    onFieldChange("deal_constraints", next);
  }

  return (
    <section style={s.panel}>
      <h2 style={s.sectionTitle}>Property and deal details</h2>
      <p style={s.sectionHint}>
        Asking price is required. Changing currency updates the label only; it
        does not convert amounts.
      </p>

      <div style={{ marginBottom: 24 }}>
        <h3 style={{ ...s.sectionTitle, fontSize: 16 }}>
          How is this deal being offered?
        </h3>
        <DealTypeSelector
          value={values.deal_type}
          disabled={disabled}
          error={errFor(errors, "deal_type")}
          onChange={onDealTypeChange}
        />
      </div>

      <FinancingFields
        dealType={values.deal_type}
        values={values.financing}
        errors={errors}
        currency={values.currency}
        disabled={disabled}
        onChange={onFinancingChange}
      />

      <h3 style={{ ...s.sectionTitle, fontSize: 16, marginTop: 24 }}>
        Property details
      </h3>
      <FieldGrid cols={2}>
        <div style={s.field}>
          <FieldLabel htmlFor="listing-property-type" required>
            Property type
          </FieldLabel>
          <TextSelect
            id="listing-property-type"
            name="property_type"
            value={values.property_type}
            disabled={disabled}
            error={Boolean(errFor(errors, "property_type"))}
            onChange={(e) => onFieldChange("property_type", e.target.value)}
          >
            <option value="">Select property type</option>
            {PROPERTY_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </TextSelect>
          <FieldError message={errFor(errors, "property_type")} />
        </div>

        <div style={s.field}>
          <FieldLabel htmlFor="listing-currency" required>
            Currency
          </FieldLabel>
          <TextSelect
            id="listing-currency"
            name="currency"
            value={values.currency}
            disabled={disabled}
            onChange={(e) => {
              onFieldChange("currency", e.target.value as Currency);
              onFieldChange("currencyOverridden", true);
            }}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </TextSelect>
        </div>

        <div style={s.field}>
          <FieldLabel htmlFor="listing-price" required>
            Asking price
          </FieldLabel>
          <TextInput
            id="listing-price"
            name="price"
            inputMode="decimal"
            autoComplete="off"
            value={values.price}
            disabled={disabled}
            error={Boolean(errFor(errors, "price"))}
            onChange={(e) => onFieldChange("price", e.target.value)}
          />
          <FieldError message={errFor(errors, "price")} />
        </div>

        <div style={s.field}>
          <FieldLabel htmlFor="listing-arv" optional>
            ARV
          </FieldLabel>
          <TextInput
            id="listing-arv"
            name="arv"
            inputMode="decimal"
            value={values.arv}
            disabled={disabled}
            error={Boolean(errFor(errors, "arv"))}
            onChange={(e) => onFieldChange("arv", e.target.value)}
          />
          <FieldError message={errFor(errors, "arv")} />
        </div>

        <div style={s.field}>
          <FieldLabel htmlFor="listing-repairs" optional>
            Estimated repairs
          </FieldLabel>
          <TextInput
            id="listing-repairs"
            name="repairs"
            inputMode="decimal"
            value={values.repairs}
            disabled={disabled}
            error={Boolean(errFor(errors, "repairs"))}
            onChange={(e) => onFieldChange("repairs", e.target.value)}
          />
          <FieldError message={errFor(errors, "repairs")} />
        </div>
      </FieldGrid>

      <h3 style={{ ...s.sectionTitle, fontSize: 16, marginTop: 24 }}>
        Property specs
      </h3>
      <FieldGrid cols={3}>
        {(
          [
            ["beds", "Beds", "numeric"],
            ["baths", "Baths", "decimal"],
            ["sqft", "Living area (sq ft)", "numeric"],
            ["lot_sqft", "Lot size (sq ft)", "numeric"],
            ["year_built", "Year built", "numeric"],
            ["garage_spaces", "Garage spaces", "numeric"],
          ] as const
        ).map(([field, label, mode]) => (
          <div key={field} style={s.field}>
            <FieldLabel htmlFor={`listing-${field}`} optional>
              {label}
            </FieldLabel>
            <TextInput
              id={`listing-${field}`}
              name={field}
              inputMode={mode === "decimal" ? "decimal" : "numeric"}
              value={values[field]}
              disabled={disabled}
              error={Boolean(errFor(errors, field))}
              onChange={(e) => onFieldChange(field, e.target.value)}
            />
            <FieldError message={errFor(errors, field)} />
          </div>
        ))}
      </FieldGrid>

      <label style={{ ...s.checkboxRow, marginTop: 16 }}>
        <input
          id="listing-age-restricted"
          name="age_restricted"
          type="checkbox"
          checked={values.age_restricted}
          disabled={disabled}
          onChange={(e) => onFieldChange("age_restricted", e.target.checked)}
          style={{
            width: 18,
            height: 18,
            marginTop: 2,
            accentColor: theme.colors.primary,
          }}
        />
        <span style={{ fontSize: 14, color: theme.colors.ink }}>
          55+ / age-restricted community
        </span>
      </label>

      <h3 style={{ ...s.sectionTitle, fontSize: 16, marginTop: 24 }}>
        Known deal constraints
      </h3>
      <p style={s.sectionHint}>Select any that apply. Optional.</p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: 8,
        }}
      >
        {DEAL_CONSTRAINTS.map(({ id, label }) => (
          <label key={id} style={s.checkboxRow}>
            <input
              type="checkbox"
              checked={values.deal_constraints.includes(id)}
              disabled={disabled}
              onChange={() => toggleConstraint(id)}
              style={{
                width: 18,
                height: 18,
                accentColor: theme.colors.primary,
              }}
            />
            <span style={{ fontSize: 14, color: theme.colors.ink }}>
              {label}
            </span>
          </label>
        ))}
      </div>
      <div style={{ ...s.field, marginTop: 12 }}>
        <FieldLabel htmlFor="listing-constraints-notes" optional>
          Constraint notes
        </FieldLabel>
        <TextTextarea
          id="listing-constraints-notes"
          name="deal_constraints_notes"
          rows={2}
          value={values.deal_constraints_notes}
          disabled={disabled}
          onChange={(e) =>
            onFieldChange("deal_constraints_notes", e.target.value)
          }
          placeholder="Additional notes about deal constraints"
        />
      </div>
    </section>
  );
}
